import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as LeadOrganisationService from 'services/agents/lead/organisation';

export const useLeadOrganisationService = () => {
  const { token } = useSelector((store) => store.user_data);

  const createLeadOrganisation = async (data, options = {}) => {
    const { error, payload: lead } = await LeadOrganisationService.create({
      ...options,
      data,
      token
    });

    if (error) {
      toast.error('Cannot create lead organisation at this time');
      return { organisation: {} };
    }

    toast.success('Lead organisation created successfully.');
    return { organisation: lead };
  };

  const deleteLeadOrganisation = async (id, options = {}) => {
    const { error } = await LeadOrganisationService.deleteOne(id, {
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to delete lead organisation at this time.');
      return false;
    }

    toast.success('Lead organisation deleted successfully');
    return true;
  };

  const fetchLeadOrganisation = async (id, options = {}) => {
    const { error, payload: organisation } = await LeadOrganisationService.readById(id, {
      ...options,
      token
    });

    if (error) {
      toast.error(error);
      return { organisation: null };
    }

    return { organisation };
  };

  const fetchOrganisations = async (options = {}) => {
    const { error, payload } = await LeadOrganisationService.read({ ...options, token });

    if (error) {
      toast.error(error);
      return { organisations: [], size: 0 };
    }

    const {
      data: organisations,
      meta: { size }
    } = payload;
    return { organisations, size };
  };

  const searchOrganisations = async (keys, keyword, options = {}) => {
    const { error, payload } = await LeadOrganisationService.search(keys, keyword, {
      ...options,
      token
    });
    if (error) {
      toast.error('Unable to fetch lead organisations at this time.');
      return { organisations: [], size: 0 };
    }

    const {
      data: organisations,
      meta: { size }
    } = payload;
    return { organisations, size };
  };

  const updateLeadOrganisation = async (id, options = {}) => {
    const { error } = await LeadOrganisationService.updateById(id, {
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to update lead organisation at this time.');
      return false;
    }

    toast.success('Lead organisation updated successfully');
    return true;
  };

  const updateLeadStage = async (id, slug, options = {}) => {
    const { error, payload } = await LeadOrganisationService.updateStage(id, {
      ...options,
      data: { slug },
      token
    });

    if (error) {
      toast.error(error);
      return false;
    }

    toast.success('Lead stage updated successfully');
    return payload || true;
  };

  const updateLeadKeyContact = async (id, contact_id, options = {}) => {
    const { error, payload } = await LeadOrganisationService.updateKeyContact(id, {
      ...options,
      data: { contact_id },
      token
    });

    if (error) {
      toast.error(error);
      return false;
    }

    toast.success('Key contact updated successfully');
    return payload || true;
  };

  const updateOrganisations = async (options = {}) => {
    const { error } = await LeadOrganisationService.update({
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to update lead organisations at this time.');
      return false;
    }

    toast.success('Lead organisations updated successfully');
    return true;
  };

  return {
    createLeadOrganisation,
    deleteLeadOrganisation,
    fetchLeadOrganisation,
    fetchOrganisations,
    searchOrganisations,
    updateLeadOrganisation,
    updateLeadStage,
    updateLeadKeyContact,
    updateOrganisations
  };
};
